import { useQuery } from '@tanstack/react-query'
import { ListFilter as ListFilterIcon, SearchX } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import * as React from 'react'

import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'

import HallOfFameProjectCard from '@/components/cards/hall-of-fame-project-card'
import PaginationComponent from '@/components/PaginationComponent'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { CourseCategory } from '@/enums/course-category.enum'
import hallOfFameProjectService from '@/services/hall-of-fame-project.service'

import { HallOfFameProject } from '@/types/hall-of-fame-project.type'
import { ListResponse } from '@/types/list.type'

/** FSDS: filter by course category (original behavior). */
const FSDS_CATEGORIES = Object.values(CourseCategory)

/** Non-FSDS: filter by entry type. */
const NON_FSDS_ENTRY_TYPES = [
  { value: '', label: 'All' },
  { value: 'project', label: 'Projects' },
  { value: 'certification', label: 'Certifications' },
  { value: 'achievement', label: 'Achievements' },
  { value: 'testimonial', label: 'Testimonials' },
]

export default function HallOfFameContainer() {
  const { isFsdsDomain } = useBranding()

  return (
    <div className="container flex flex-col lg:flex-row">
      {isFsdsDomain ? null : <NonFsdsNavbar />}
      <WrapperContentProjects>
        {isFsdsDomain ? null : <NonFsdsNavbarMobile />}
        <ListProjects />
      </WrapperContentProjects>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  FSDS sidebar & mobile — filter by course category                 */
/* ------------------------------------------------------------------ */

// eslint-disable-next-line unused-imports/no-unused-vars
function FsdsNavbar() {
  const params = useSearchParams()
  const filter = params.get('category') ?? ''
  const { text } = useLocale()

  return (
    <aside className="hidden w-64 flex-shrink-0 lg:block">
      <div className="sticky top-20 h-screen overflow-y-auto p-6">
        <nav>
          <h2 className="mb-2 text-lg font-semibold flex items-center gap-2">
            <ListFilterIcon className="mr-2 h-4 w-4" />
            {text.HALL_OF_FAME.FILTER_BY}
          </h2>
          <div className="space-y-1">
            <Link
              href="/hall-of-fame"
              className={`block py-2 text-sm hover:underline hover:text-primary cursor-pointer ${!filter ? 'font-semibold text-primary' : ''}`}
              scroll={false}
            >
              {text.HALL_OF_FAME.ALL}
            </Link>
            {FSDS_CATEGORIES.map(category => (
              <Link
                key={category}
                href={{
                  pathname: '/hall-of-fame',
                  query: { category },
                }}
                className={`block py-2 text-sm hover:underline hover:text-primary cursor-pointer ${
                  filter === category ? 'font-semibold text-primary' : ''
                }`}
                scroll={false}
              >
                {category}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </aside>
  )
}

// eslint-disable-next-line
function FsdsNavbarMobile() {
  const router = useRouter()
  const params = useSearchParams()
  const filter = params.get('category') ?? ''
  const { text } = useLocale()

  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="lg:hidden">
        <Select
          value={filter || 'all'}
          onValueChange={value => {
            if (value === 'all') {
              router.push('/hall-of-fame', undefined, { scroll: false })
            } else {
              router.push(
                {
                  pathname: '/hall-of-fame',
                  query: { category: value },
                },
                undefined,
                { scroll: false }
              )
            }
          }}
        >
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder={text.HALL_OF_FAME.FILTER_BY} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{text.HALL_OF_FAME.ALL}</SelectItem>
            {FSDS_CATEGORIES.map(category => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Non-FSDS sidebar & mobile — filter by entry type                  */
/* ------------------------------------------------------------------ */

function NonFsdsNavbar() {
  const params = useSearchParams()
  const filter = params.get('type') ?? ''
  const { text } = useLocale()

  return (
    <aside className="hidden w-64 flex-shrink-0 lg:block">
      <div className="sticky top-20 h-screen overflow-y-auto p-6">
        <nav>
          <h2 className="mb-2 text-lg font-semibold flex items-center gap-2">
            <ListFilterIcon className="mr-2 h-4 w-4" />
            {text.HALL_OF_FAME.FILTER_BY}
          </h2>
          <div className="space-y-1">
            {NON_FSDS_ENTRY_TYPES.map(({ value, label }) => (
              <Link
                key={value || 'all'}
                href={{
                  pathname: '/hall-of-fame',
                  query: value ? { type: value } : undefined,
                }}
                className={`block py-2 text-sm hover:underline hover:text-primary cursor-pointer ${
                  filter === value ? 'font-semibold text-primary' : ''
                }`}
                scroll={false}
              >
                {label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </aside>
  )
}

function NonFsdsNavbarMobile() {
  const router = useRouter()
  const params = useSearchParams()
  const filter = params.get('type') ?? ''
  const { text } = useLocale()

  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="lg:hidden">
        <Select
          value={filter || 'all'}
          onValueChange={value => {
            if (value === 'all') {
              router.push('/hall-of-fame', undefined, { scroll: false })
            } else {
              router.push(
                {
                  pathname: '/hall-of-fame',
                  query: { type: value },
                },
                undefined,
                { scroll: false }
              )
            }
          }}
        >
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder={text.HALL_OF_FAME.FILTER_BY} />
          </SelectTrigger>
          <SelectContent>
            {NON_FSDS_ENTRY_TYPES.map(({ value, label }) => (
              <SelectItem key={value || 'all'} value={value || 'all'}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Shared components                                                  */
/* ------------------------------------------------------------------ */

function WrapperContentProjects({ children }: { children: React.ReactNode }) {
  return <div className="flex-1 lg:overflow-y-auto">{children}</div>
}

function ProjectCardSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden animate-pulse">
      <CardHeader className="border-b p-0">
        <div className="relative aspect-[4/3] w-full bg-muted" />
      </CardHeader>
      <CardContent className="flex-1 p-6">
        <div className="flex justify-between mb-4 md:mb-6">
          <div className="h-5 w-20 bg-muted rounded-full" />
          <div className="h-5 w-24 bg-muted rounded-lg" />
        </div>
        <div className="h-8 w-3/4 bg-muted rounded-lg mb-2" />
        <div className="mt-4 flex items-center gap-4">
          <div className="h-5 w-16 bg-muted rounded-lg" />
          <div className="h-5 w-20 bg-muted rounded-lg" />
        </div>
      </CardContent>
      <CardFooter>
        <div className="flex w-full items-center justify-between">
          <div className="h-6 w-32 bg-muted rounded-lg" />
          <div className="flex gap-2">
            <div className="h-8 w-8 bg-muted rounded-lg" />
            <div className="h-8 w-8 bg-muted rounded-lg" />
          </div>
        </div>
      </CardFooter>
    </Card>
  )
}

function EmptyProjectsMessage({ filter }: { filter: string }) {
  const { text } = useLocale()
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-muted p-4 mb-4">
        <SearchX className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">{text.HALL_OF_FAME.NO_PROJECT_FOUND}</h3>
      <p className="text-muted-foreground max-w-[500px]">
        {filter
          ? text.HALL_OF_FAME.NO_PROJECT_FOUND_TEXT_1.replace('[Index]', `"${filter}"`)
          : text.HALL_OF_FAME.NO_PROJECT_FOUND_TEXT_2}
      </p>
    </div>
  )
}

/**
 * Shared card grid — adapts search field based on domain.
 *
 * FSDS: filters by `course` field using `category` query param.
 * Non-FSDS: filters by `type` field using `type` query param.
 */
function ListProjects() {
  const params = useSearchParams()
  const { isFsdsDomain } = useBranding()

  const page = Number(params.get('page')) || 1
  const pageSize = Number(params.get('perPage')) || 9

  // FSDS uses ?category=..., non-FSDS uses ?type=...
  const fsdsFilter = params.get('category') ?? ''
  const nonFsdsFilter = params.get('type') ?? ''
  const activeFilter = isFsdsDomain ? fsdsFilter : nonFsdsFilter
  const searchField = isFsdsDomain ? 'course' : 'type'

  const router = useRouter()
  const { locale } = router
  const { text } = useLocale()
  const hallOfFameText = text.HALL_OF_FAME as typeof text.HALL_OF_FAME & {
    ERROR_OCCURRED?: string
    LOAD_ERROR?: string
    RELOAD?: string
  }

  const {
    data: projects,
    isLoading,
    isError,
    error,
  } = useQuery<ListResponse<HallOfFameProject>>({
    queryKey: ['hall-of-fame', page, pageSize, activeFilter, searchField],
    queryFn: () => {
      return hallOfFameProjectService.getProjects({
        queryParams: {
          searchFields: activeFilter ? [searchField] : undefined,
          search: activeFilter || undefined,
          pageNumber: page,
          pageSize,
          sort: '-stars',
        },
        locale,
      })
    },
  })

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <h3 className="text-lg font-semibold text-destructive mb-2">
          {hallOfFameText.ERROR_OCCURRED || 'An error occurred'}
        </h3>
        <p className="text-muted-foreground mb-4">
          {(error as Error)?.message || hallOfFameText.LOAD_ERROR || 'Could not load project data'}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {hallOfFameText.RELOAD || 'Reload page'}
        </Button>
      </div>
    )
  }

  const hasProjects = projects?.data && projects.data.length > 0

  return (
    <div className="pb-12 sm:pb-20">
      {isLoading ? (
        <div className="grid gap-6 sm:gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: pageSize }).map((_, index) => (
            <ProjectCardSkeleton key={index} />
          ))}
        </div>
      ) : !hasProjects ? (
        <EmptyProjectsMessage filter={activeFilter} />
      ) : (
        <>
          <div className="grid gap-6 sm:gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {projects.data.map(project => (
              <HallOfFameProjectCard key={project.id} project={project} />
            ))}
          </div>
          <div className="mt-6 md:mt-12 lg:mt-20 flex justify-end">
            <PaginationComponent
              currentPage={page}
              pageSize={pageSize}
              itemCount={projects.meta?.totalItems || pageSize}
            />
          </div>
        </>
      )}
    </div>
  )
}
